import { useEffect } from "react";
import { useParams } from "react-router-dom";

import Loader from "components/Loader";
import PageNotFound from "pages/404";

import { useGetEmployeeAccess } from "./useGetEmployeeAccess";
import { useGetEmployeeAppAccess } from "./useGetEmployeeAppAccess";
import { useGetUserAssociatedBUList } from "./useGetUserAssociatedBUList";
import { queryGetBUDetail } from "@type/api/businessUnit/getBUDetail";
import {
  useAuthStore,
  useBusinessUnitStore,
  useLayoutStore,
} from "@type/store";
import { useQueryWithCallbacks } from "lib/query/useQueryWithCallbacks";

interface BusinessUnitDataWrapperProps {
  children: React.ReactNode;
}

const BusinessUnitDataWrapper = ({
  children,
}: BusinessUnitDataWrapperProps) => {
  const { loading, mapBUList } = useGetUserAssociatedBUList({
    page: 1,
    perPage: 6,
  });

  const { buSlug } = useParams();
  const isDataNotFound = useLayoutStore((state) => state.isDataNotFound);
  const selectedBU = useBusinessUnitStore((state) => state.selectedBU);
  const onSetLoading = useBusinessUnitStore((state) => state.onSetLoading);
  const onSetMapBUList = useBusinessUnitStore((state) => state.onSetMapBUList);
  const user = useAuthStore((state) => state.user);
  const onSetSelectedBU = useBusinessUnitStore(
    (state) => state.onSetSelectedBU
  );
  const onSetIsDataNotFound = useLayoutStore(
    (state) => state.onSetIsDataNotFound
  );

  const { isLoading } = useQueryWithCallbacks({
    queryKey: queryGetBUDetail._key(buSlug || ""),
    enabled: !!buSlug,
    queryFn: () => queryGetBUDetail(buSlug || ""),
    onSuccess: (res) => {
      onSetSelectedBU({
        ...res,
        slug: res.idUnique,
      });
      onSetIsDataNotFound(false);
      onSetLoading(false);
    },
    onError: () => {
      onSetIsDataNotFound(true);
      onSetLoading(false);
    },
  });

  useGetEmployeeAccess({
    params: {
      buId: selectedBU?.idUnique || "",
      userId: user.idUnique,
    },
  });

  useGetEmployeeAppAccess({
    params: {
      idBu: selectedBU?.idUnique || "",
      data: {
        idUser: user.idUnique,
      },
    },
  });

  useEffect(() => {
    if (!loading && mapBUList !== undefined) {
      onSetMapBUList(mapBUList);
    }
  }, [loading, mapBUList, onSetMapBUList]);

  if (isLoading) {
    return <Loader />;
  }

  if (isDataNotFound) {
    return <PageNotFound />;
  }

  return <>{children}</>;
};

export default BusinessUnitDataWrapper;
